import { Directive, ElementRef, forwardRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CurrencyMaskConfig } from './currency-mask.config';

@Directive({
  standalone: true,
  selector: '[currencyMask]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CurrencyMaskDirective),
      multi: true,
    },
  ],
})
export class CurrencyMaskDirective implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() options: CurrencyMaskConfig = {};

  private inputElement: HTMLInputElement;
  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  private defaultConfig: Required<Omit<CurrencyMaskConfig, 'min' | 'max'>> & { min?: number; max?: number } = {
    align: 'left',
    allowNegative: false,
    allowZero: false,
    decimal: '.',
    nullable: false,
    precision: 2,
    prefix: '$',
    suffix: '',
    thousands: ',',
    inputMode: 1,
  };

  private config!: Required<Omit<CurrencyMaskConfig, 'min' | 'max'>> & { min?: number; max?: number };

  constructor(private el: ElementRef<HTMLInputElement>) {
    this.inputElement = el.nativeElement;
  }

  ngOnInit() {
    this.config = { ...this.defaultConfig, ...this.options };
    // Set initial value if present
    const initialValue = this.inputElement.value;
    if (initialValue) {
      this.formatValue(initialValue);
    }
  }

  ngOnDestroy() {
    // Cleanup if needed
  }

  @HostListener('input', ['$event'])
  onInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const cursorPosition = input.selectionStart || 0;
    const oldValue = input.value;
    const newValue = this.formatValue(oldValue);

    // Restore cursor position
    setTimeout(() => {
      const newCursorPosition = this.calculateNewCursorPosition(oldValue, newValue, cursorPosition);
      input.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);

    this.updateModelValue(newValue);
  }

  @HostListener('blur')
  onBlur() {
    this.onTouched();
    const value = this.inputElement.value;
    if (value) {
      const formatted = this.formatValue(value);
      this.inputElement.value = formatted;
      this.updateModelValue(formatted);
    }
  }

  @HostListener('focus')
  onFocus() {
    // Optionally select all on focus
  }

  writeValue(value: number | null): void {
    if (value === null || value === undefined) {
      if (this.config.nullable) {
        this.inputElement.value = '';
        return;
      }
      this.inputElement.value = this.formatNumber(0);
      return;
    }

    const formatted = this.formatNumber(value);
    this.inputElement.value = formatted;
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.inputElement.disabled = isDisabled;
  }

  private formatValue(value: string): string {
    // Remove all non-numeric characters except decimal point and minus
    let cleaned = value.replace(/[^\d.-]/g, '');

    // Handle negative sign
    const isNegative = cleaned.startsWith('-');
    if (isNegative && !this.config.allowNegative) {
      cleaned = cleaned.replace('-', '');
    }

    // Remove all but the first decimal point
    let parts = cleaned.split(this.config.decimal);
    if (parts.length > 2) {
      cleaned = parts[0] + this.config.decimal + parts.slice(1).join('');
      parts = cleaned.split(this.config.decimal);
    }

    // Limit decimal places
    if (parts.length === 2) {
      parts[1] = parts[1].slice(0, this.config.precision);
      cleaned = parts[0] + this.config.decimal + parts[1];
    }

    // If empty after cleaning and nullable, return empty
    if (!cleaned || cleaned === '-' || cleaned === this.config.decimal) {
      if (this.config.nullable) {
        return '';
      }
      cleaned = '0';
    }

    // Parse to number
    let numValue = parseFloat(cleaned);
    if (isNaN(numValue)) {
      if (this.config.nullable) {
        return '';
      }
      numValue = 0;
    }

    // Apply negative if needed
    if (isNegative && this.config.allowNegative && numValue > 0) {
      numValue = -numValue;
    }

    // Apply min/max constraints
    if (this.config.min !== undefined && numValue < this.config.min) {
      numValue = this.config.min;
    }
    if (this.config.max !== undefined && numValue > this.config.max) {
      numValue = this.config.max;
    }

    // Check allowZero
    if (!this.config.allowZero && numValue === 0) {
      if (this.config.nullable) {
        return '';
      }
      // Don't force a minimum if it would violate min constraint
      if (this.config.min === undefined || this.config.min <= 0.01) {
        numValue = 0.01;
      } else {
        numValue = this.config.min;
      }
    }

    return this.formatNumber(numValue);
  }

  private formatNumber(value: number): string {
    if (isNaN(value)) {
      return this.config.nullable ? '' : this.formatNumber(0);
    }

    // Round to precision
    const rounded = Math.round(value * Math.pow(10, this.config.precision)) / Math.pow(10, this.config.precision);

    // Format with decimal places
    const fixed = rounded.toFixed(this.config.precision);

    // Split into integer and decimal parts
    const parts = fixed.split('.');
    let integerPart = parts[0];
    const decimalPart = parts[1] || '';

    // Add thousands separator
    if (this.config.thousands) {
      integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, this.config.thousands);
    }

    // Handle negative sign
    if (rounded < 0) {
      integerPart = integerPart.replace('-', '');
    }

    // Build the formatted string
    let formatted = '';
    if (this.config.prefix) {
      formatted += this.config.prefix;
    }
    if (rounded < 0 && this.config.allowNegative) {
      formatted += '-';
    }
    formatted += integerPart;
    if (this.config.precision > 0 && decimalPart) {
      formatted += this.config.decimal + decimalPart;
    } else if (this.config.precision > 0) {
      formatted += this.config.decimal + '0'.repeat(this.config.precision);
    }
    if (this.config.suffix) {
      formatted += this.config.suffix;
    }

    return formatted;
  }

  private updateModelValue(formattedValue: string): void {
    if (!formattedValue || formattedValue.trim() === '') {
      if (this.config.nullable) {
        this.onChange(null);
        return;
      }
      this.onChange(0);
      return;
    }

    // Extract numeric value from formatted string
    let cleaned = formattedValue.replace(/[^\d.-]/g, '');
    const isNegative = cleaned.startsWith('-') || formattedValue.includes('(');

    // Remove negative sign for parsing
    cleaned = cleaned.replace('-', '');

    const numValue = parseFloat(cleaned);

    if (isNaN(numValue)) {
      this.onChange(this.config.nullable ? null : 0);
      return;
    }

    const finalValue = isNegative && this.config.allowNegative ? -numValue : numValue;

    // Apply min/max validation
    let validatedValue = finalValue;
    if (this.config.min !== undefined && validatedValue < this.config.min) {
      validatedValue = this.config.min;
    }
    if (this.config.max !== undefined && validatedValue > this.config.max) {
      validatedValue = this.config.max;
    }

    this.onChange(validatedValue);
  }

  private calculateNewCursorPosition(oldValue: string, newValue: string, oldCursorPosition: number): number {
    // Simple cursor position calculation
    // Count non-formatting characters before cursor in old value
    const oldPrefix = oldValue.substring(0, oldCursorPosition);
    const oldNonFormatting = oldPrefix.replace(/[^\d.-]/g, '').length;

    // Find position in new value with same number of non-formatting characters
    let count = 0;
    for (let i = 0; i < newValue.length; i++) {
      if (/[\d.-]/.test(newValue[i])) {
        count++;
        if (count >= oldNonFormatting) {
          return i + 1;
        }
      }
    }

    return newValue.length;
  }
}
